import { ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';

import { BOMRow, ExtractedTableType, TableCell, TableRow } from '@frontend/api';

import {
  HeaderCellRender,
  ItemNoCellRender,
  ItemNoHeaderCellRender,
} from 'modules/extractedTables/cells';

/**
 * Builds a dictionary mapping BOM row IDs to their index in the flattened BOM data.
 * This is used to quickly look up the index of a BOM row given its ID.
 *
 * @param rows - The BOM rows to build the dictionary for.
 * @param parentPath - The parent path of the current rows, used to construct the full path.
 * @param startIndex - The starting index for the current set of rows.
 * @returns A tuple containing the dictionary and the next starting index.
 */
const buildBomRowIdxByRowIdMap = (
  rows: BOMRow[],
  parentPath: string,
  startIndex: number,
): [Record<string, number>, number] => {
  let dict: Record<string, number> = {};
  let currentIndex = startIndex;

  rows.forEach((row, i) => {
    // For sibling #i (0-based), the display number is i+1
    const displayNum = i + 1;

    // If we're at top-level (parentPath==""), the new path is just "1","2", etc.
    // If we're in deeper levels, it's e.g. "1.2","1.3","2.1.1", etc.
    const childPath =
      parentPath === '' ? `${displayNum}` : `${parentPath}.${displayNum}`;

    dict[childPath] = currentIndex;
    currentIndex++;

    // Recurse for children
    if (row.children && row.children.length > 0) {
      const [childDict, nextIndex] = buildBomRowIdxByRowIdMap(
        row.children,
        childPath,
        currentIndex,
      );
      dict = { ...dict, ...childDict };
      currentIndex = nextIndex;
    }
  });

  return [dict, currentIndex];
};

/**
 * Checks the content of table rows to see if they are equal.
 * Does NOT check bounding box info.
 * @param row1
 * @param row2
 */
const areTableRowsContentEqual = (row1: TableRow, row2: TableRow): boolean => {
  // Helper function to compare two TableCell objects based on content and headerKey
  const areCellsEqual = (
    cell1: TableCell | null,
    cell2: TableCell | null,
  ): boolean => {
    if (cell1 === cell2) return true;
    if (cell1 === null || cell2 === null) return false;
    return (
      cell1.content === cell2.content && cell1.headerKey === cell2.headerKey
    );
  };

  // Compare individual cells in the cells array
  if (row1.cells.length !== row2.cells.length) return false;
  for (let i = 0; i < row1.cells.length; i++) {
    if (!areCellsEqual(row1.cells[i], row2.cells[i])) return false;
  }

  // If all checks passed, the rows are equal
  return true;
};

export const useExtractedTableConfig = ({
  extractedTable,
}: {
  extractedTable: ExtractedTableType;
}) => {
  // Generate column config and data
  const { rawColumns, bomColumns, rawData, bomData, bomRowIdxsByRowId } =
    useMemo<{
      rawColumns: ColumnDef<TableRow>[];
      bomColumns: ColumnDef<BOMRow>[];
      rawData: TableRow[];
      bomData: BOMRow[];
      bomRowIdxsByRowId: Record<string, number>;
    }>(() => {
      const bomColumns: ColumnDef<BOMRow>[] = [];
      const bomData: BOMRow[] = [];
      const rawColumns: ColumnDef<TableRow>[] = [];
      const rawData: TableRow[] = [];

      /**
       * Logic for determining bom columns and data
       * - if no bomHeader, dont create any bom columns or data
       * - exclude the bomHeader from the rows
       * - include headers as they may actually contain valid data
       * - ignore title rows for now
       *
       * Logic for creating raw columns and data
       * - if a bomHeader is specified, use the raw values of the bomHeader to create column config and include the headerRows in the data
       * - if one header row, specify column headers as that data, and exclude header rows from data
       * - if multiple header rows, leave the column headers as unspecified names and include header rows in the data
       * - if no header rows, leave column headers as unspecified and include all rows as data
       * - ignore title rows for now
       */
      if (extractedTable.bomHeader && extractedTable.bomRows) {
        bomColumns.push(
          {
            id: 'id',
            header: (props) =>
              ItemNoHeaderCellRender({
                headerName: 'Item No.',
                ...props,
              }),
            cell: ({ row, cell, table }) => {
              return ItemNoCellRender({ row, cell, table });
            },
          },
          {
            id: `partNumber`,
            header: (props) =>
              HeaderCellRender({
                headerName: 'Part Number',
                ...props,
              }),
            accessorFn: (row) => {
              return row.partNumber ?? '';
            },
          },
          {
            id: `revision`,
            header: (props) =>
              HeaderCellRender({
                headerName: 'Revision',
                ...props,
              }),
            accessorFn: (row) => {
              return row.revision ?? '';
            },
          },
          {
            id: `description`,
            header: (props) =>
              HeaderCellRender({
                headerName: 'Description',
                ...props,
              }),
            accessorFn: (row) => {
              return row.description ?? '';
            },
          },
          {
            id: `count`,
            header: (props) =>
              HeaderCellRender({
                headerName: 'Quantity',
                ...props,
              }),
            accessorFn: (row) => {
              return row.count;
            },
          },
        );

        bomData.push(...extractedTable.bomRows);

        const columns: ColumnDef<TableRow>[] =
          extractedTable.bomHeader.row.cells.map((headerCell) => ({
            id: `dynamic_col_${headerCell.headerKey}`,
            header: (props) =>
              HeaderCellRender({
                headerName:
                  headerCell.content ?? `Column ${headerCell.headerKey}`,
                ...props,
              }),
            accessorFn: (row) => row.cells[headerCell.headerKey]?.content ?? '',
          }));
        rawColumns.push(...columns);

        for (const row of extractedTable.table.headerRows.concat(
          extractedTable.table.rows,
        )) {
          if (!areTableRowsContentEqual(extractedTable.bomHeader.row, row)) {
            rawData.push(row);
          }
        }
      } else if (extractedTable.table.headerRows.length === 1) {
        const headerRow = extractedTable.table.headerRows[0];
        const columns: ColumnDef<TableRow>[] = headerRow.cells.map(
          (headerCell) => ({
            id: `dynamic_col_${headerCell.headerKey}`,
            header: (props) =>
              HeaderCellRender({
                headerName:
                  headerCell.content ?? `Column ${headerCell.headerKey}`,
                ...props,
              }),
            accessorFn: (row) => row.cells[headerCell.headerKey]?.content ?? '',
          }),
        );
        rawColumns.push(...columns);
        rawData.push(...extractedTable.table.rows);
      } else if (extractedTable.table.rows.length > 0) {
        const firstRow = extractedTable.table.rows[0];
        const columns: ColumnDef<TableRow>[] = firstRow.cells.map(
          (headerCell) => ({
            id: `dynamic_col_${headerCell.headerKey}`,
            header: (props) =>
              HeaderCellRender({
                headerName: `Column ${headerCell.columnIndex}`,
                ...props,
              }),
            accessorFn: (row) => row.cells[headerCell.headerKey]?.content ?? '',
          }),
        );
        rawColumns.push(...columns);
        rawData.push(
          ...extractedTable.table.headerRows,
          ...extractedTable.table.rows,
        );
      }

      // Create dictionary to map ids to row indices
      // NB: We start at 1, because index is root, and start at the children of the first BOM row because we don't want to include the root
      const [bomRowIdxsByRowId] = buildBomRowIdxByRowIdMap(
        bomData[0]?.children ?? [],
        '',
        1,
      );

      return { rawColumns, bomColumns, rawData, bomData, bomRowIdxsByRowId };
    }, [extractedTable]);

  return { rawColumns, bomColumns, rawData, bomData, bomRowIdxsByRowId };
};
